import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVisualViewport } from "@/hooks/use-visual-viewport";

interface Message {
  id: number;
  role: "user" | "bot";
  content: string;
}

function LogoMark() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="16" r="15" stroke="hsl(262 83% 58%)" strokeWidth="2" fill="none"/>
      <circle cx="16" cy="16" r="9" stroke="hsl(262 83% 58%)" strokeWidth="2" fill="none" opacity="0.5"/>
      <circle cx="16" cy="16" r="3.5" fill="hsl(262 83% 58%)"/>
      <path d="M16 1.5 A14.5 14.5 0 0 1 30.5 16" stroke="hsl(262 83% 58%)" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

let nextId = 1;

export default function PreviewChatPage() {
  const [messages, setMessages] = useState<Message[]>([
    { id: nextId++, role: "bot", content: "Olá! Este é o modo sandbox. Digite qualquer coisa e o bot irá ecoar sua mensagem." },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const vp = useVisualViewport();

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    if (vp !== null) {
      scrollToBottom("instant");
      window.scrollTo(0, 0);
    }
  }, [vp?.height, vp?.offsetTop]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [inputValue]);

  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    const message = inputValue.trim();
    if (!message || isLoading) return;

    setInputValue("");
    setMessages((prev) => [...prev, { id: nextId++, role: "user", content: message }]);
    setIsLoading(true);

    setTimeout(() => {
      setMessages((prev) => [...prev, { id: nextId++, role: "bot", content: `Echo: ${message}` }]);
      setIsLoading(false);
    }, 1000);
  };

  const containerStyle: React.CSSProperties = vp
    ? {
        position: "fixed",
        top: `${vp.offsetTop}px`,
        left: 0,
        right: 0,
        height: `${vp.height}px`,
      }
    : { minHeight: "100dvh" };

  return (
    <div
      className="flex flex-col bg-background max-w-2xl mx-auto w-full overflow-hidden"
      style={containerStyle}
    >
      <header className="px-4 py-3 border-b bg-card sticky top-0 z-10 flex items-center justify-between shrink-0 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2.5">
          <LogoMark />
          <span className="font-semibold text-[15px] text-foreground tracking-tight">Avaliação</span>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-700 border border-amber-300">
            Sandbox
          </span>
          <span className="text-[10px] text-muted-foreground">Bloco 1 de 4 · 25% concluído</span>
        </div>
      </header>

      <div className="h-1 bg-muted relative overflow-hidden shrink-0">
        <div
          className="absolute inset-y-0 left-0 bg-primary transition-all duration-700 ease-out"
          style={{ width: "25%" }}
        />
      </div>

      <div
        className="flex-1 px-4 py-4 space-y-4 overflow-y-auto sm:px-5 sm:py-5"
        role="log"
        aria-live="polite"
        aria-label="Conversa"
      >
        {messages.map((msg) => {
          const isUser = msg.role === "user";
          return (
            <div
              key={msg.id}
              className={cn(
                "flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300",
                isUser ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed sm:text-[14.5px] sm:max-w-[82%]",
                  isUser
                    ? "bg-primary text-primary-foreground rounded-tr-sm"
                    : "bg-white shadow-sm text-card-foreground rounded-tl-sm"
                )}
              >
                {msg.content}
              </div>
            </div>
          );
        })}
        {isLoading && (
          <div className="flex w-full justify-start animate-in fade-in">
            <div className="px-4 py-3 rounded-2xl bg-white shadow-sm rounded-tl-sm flex space-x-1.5 items-center h-[48px]">
              <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} className="h-2" />
      </div>

      <div className="px-3 py-3 bg-card border-t shrink-0 sm:px-4 sm:py-4">
        <form onSubmit={handleSend} className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend(e as unknown as React.FormEvent);
              }
            }}
            placeholder="Digite sua resposta..."
            className="flex-1 min-h-[48px] sm:min-h-[44px] max-h-[120px] rounded-xl px-4 py-3 bg-white border-border focus-visible:border-primary focus-visible:ring-primary/15 text-[15px] sm:text-[14.5px] resize-none overflow-hidden leading-normal"
            disabled={isLoading}
            onFocus={() => {
              window.scrollTo(0, 0);
              setTimeout(() => {
                window.scrollTo(0, 0);
                scrollToBottom("instant");
              }, 150);
            }}
            autoComplete="off"
            aria-label="Sua resposta"
            rows={1}
          />
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-12 w-12 rounded-xl shrink-0 sm:h-11 sm:w-11"
            disabled
            aria-label="Gravar resposta por voz (indisponível no sandbox)"
          >
            <Mic className="h-4 w-4" />
          </Button>
          <Button
            type="submit"
            size="icon"
            className="h-12 w-12 rounded-xl shrink-0 sm:h-11 sm:w-11"
            disabled={!inputValue.trim() || isLoading}
            aria-label="Enviar resposta"
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
